/**
 * Global sync orchestrator.
 *
 * Listens to network reconnect events and fans out to every offline service's
 * syncAll(). Replaces the per-component reconnect logic in SyncStatusIndicator,
 * which only fired while that component was mounted — meaning users away from
 * field pages would never auto-sync.
 *
 * The service worker's `sync-mutations` background-sync handler is intentionally
 * a NO-OP (see service-worker.ts SW-001) because BackgroundSyncPlugin cached
 * synthetic 503s as real responses. This app-level orchestrator is the single
 * source of truth for retry.
 */
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { damageAssessmentOfflineService } from './damage-assessment.service';
import { offlineSupplyRequestService } from './supply-request.service';
import { pdaZoneOfflineService } from './pda-zone.service';
import { boardOfflineService } from './board.service';
import { ics214OfflineService } from './ics214.service';
import { suasOfflineService } from './suas-offline.service';
import { personnelPositionSyncService } from './personnel-position-sync.service';
import { pendingMutationsSyncService } from './pending-mutations.service';
import { taskOfflineService } from './task.service';
import { infrastructureOfflineService } from './infrastructure.service';
import { femaCostOfflineService } from './fema-cost.service';
import { incidentOfflineService } from './incident-offline.service';
import { formOfflineService } from './form-offline.service';
import { shelterCheckinOfflineService } from './shelter-checkin.service';

const RECONNECT_DEBOUNCE_MS = 3000;
const MIN_INTERVAL_MS = 15_000;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const DONE_HOLD_MS = 3000;
const ERROR_HOLD_MS = 5000;

export type SyncPhase = 'idle' | 'syncing' | 'done' | 'error';

export interface SyncOrchestratorState {
  phase: SyncPhase;
  lastSyncAt: number | null;
  lastError: string | null;
  errorCount: number;
}

export interface SyncResult {
  skipped?: 'offline' | 'recent' | 'in-flight' | 'no-tenant';
  errors: string[];
}

type Listener = (state: SyncOrchestratorState) => void;

class SyncOrchestratorService {
  private state: SyncOrchestratorState = {
    phase: 'idle',
    lastSyncAt: null,
    lastError: null,
    errorCount: 0,
  };
  private listeners = new Set<Listener>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<SyncResult> | null = null;
  private initialized = false;
  private reconcileReady: Promise<unknown> | null = null;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Crash recovery, BEFORE the network listener is wired so a reconnect-
    // triggered sync cannot race it. A session killed mid-replay (iOS/Android
    // suspend, tab reload, service-worker auto-update reload) used to leave a
    // queued mutation stranded at status 'syncing' — never retried, never
    // counted, pill showing "Synced" — silently losing a PDA zone completion or
    // a shelter check-out. This returns those rows to 'pending'.
    this.reconcileReady = pendingMutationsSyncService.reconcileStale().catch((err) => {
      console.warn('[SyncOrchestrator] stale mutation reconciliation failed:', err);
      return 0;
    });

    networkService.addListener((connected) => {
      if (connected) {
        this.scheduleReconnectSync();
      } else if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
    });
  }

  private scheduleReconnectSync(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    const delay = Math.max(RECONNECT_DEBOUNCE_MS, this.computeBackoff());
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.syncNow();
    }, delay);
  }

  private computeBackoff(): number {
    if (this.state.errorCount === 0) return 0;
    return Math.min(RECONNECT_DEBOUNCE_MS * 2 ** this.state.errorCount, MAX_BACKOFF_MS);
  }

  async syncNow(force = false): Promise<SyncResult> {
    if (!networkService.isOnline()) {
      return { skipped: 'offline', errors: [] };
    }
    if (this.inFlight) {
      return { skipped: 'in-flight', errors: [] };
    }
    if (
      !force &&
      this.state.lastSyncAt &&
      Date.now() - this.state.lastSyncAt < MIN_INTERVAL_MS &&
      this.state.phase !== 'error'
    ) {
      return { skipped: 'recent', errors: [] };
    }

    this.cancelHold();
    this.setState({ phase: 'syncing', lastError: null });

    this.inFlight = this.fanOut().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async fanOut(): Promise<SyncResult> {
    const tenantId = useTenantStore.getState().activeTenant?.id || '';
    const errors: string[] = [];

    // Nothing is ever sent without an explicit tenant: with no active tenant the api client
    // sends no X-Tenant-ID and the server falls back to the token's default tenant (e.g.
    // after MEMBERSHIP_REMOVED cleared the active one). Every row stays pending; each send
    // path's gate (client-id.ts mustDeferSend, 'no_tenant') would defer it anyway.
    if (!tenantId) {
      this.setState({ phase: 'idle' });
      return { skipped: 'no-tenant', errors };
    }

    // Make sure crash-stranded mutations have been returned to 'pending' before
    // pendingMutationsSyncService.syncAll() runs, or they wait another cycle.
    if (this.reconcileReady) {
      await this.reconcileReady;
    }

    // Sync incidents FIRST (awaited) so offline-created incidents get their real
    // server id AND remap that id onto their dependents (tasks, damage
    // assessments, supply requests, ...) before those dependents sync — avoids
    // a child being POSTed with a stale offline incident_id.
    try {
      const incidentRes = await incidentOfflineService.syncAll();
      if (incidentRes && typeof incidentRes === 'object' && 'errors' in incidentRes) {
        const ie = (incidentRes as { errors?: string[] }).errors;
        if (ie && ie.length > 0) errors.push(...ie);
      }
    } catch (e) {
      errors.push(`Incident sync failed: ${String(e)}`);
    }

    const tasks: Array<Promise<unknown>> = [
      damageAssessmentOfflineService.syncAll(),
      taskOfflineService.syncAll(),
      offlineSupplyRequestService.syncAll(),
      pdaZoneOfflineService.syncBreadcrumbs(),
      tenantId ? boardOfflineService.syncAll(tenantId) : Promise.resolve(),
      tenantId ? ics214OfflineService.syncAll(tenantId) : Promise.resolve(),
      suasOfflineService.syncSnapshots(),
      personnelPositionSyncService.syncAll(),
      infrastructureOfflineService.syncAll('critical'),
      infrastructureOfflineService.syncAll('road-closures'),
      infrastructureOfflineService.syncAll('utility-outages'),
      femaCostOfflineService.syncAll(),
      // Shelter check-ins and offline ICS forms used to be drained ONLY by the
      // network listener inside useOfflineShelterCheckin / useOfflineForms, which
      // exist only while the Field Shelter Check-in / Field Forms page is mounted.
      // A worker who checked residents in offline and then navigated away (or the
      // app was killed and reopened) reconnected with the roster still sitting in
      // IndexedDB and nothing anywhere to upload it — during an evacuation that is
      // a life-safety data-loss path. If this is reverted, the records go silently
      // unsent again. tenantId gating matches boardOfflineService/ics214 above:
      // formOfflineService sends X-Tenant-ID from the store and api.post injects
      // the ambient tenant, so with no active tenant both would only POST an empty
      // tenant header and fail.
      tenantId ? shelterCheckinOfflineService.syncAll() : Promise.resolve(),
      tenantId ? formOfflineService.syncAll() : Promise.resolve(),
      pendingMutationsSyncService.syncAll(),
    ];

    const results = await Promise.allSettled(tasks);
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        errors.push(`Sync slot ${i} failed: ${String(r.reason)}`);
      } else if (r.value && typeof r.value === 'object' && 'errors' in r.value) {
        const sv = r.value as { errors?: string[] };
        if (sv.errors && sv.errors.length > 0) {
          errors.push(...sv.errors);
        }
      }
    });

    const now = Date.now();
    if (errors.length === 0) {
      this.setState({ phase: 'done', lastSyncAt: now, lastError: null, errorCount: 0 });
      this.scheduleHold('idle', DONE_HOLD_MS);
    } else {
      this.setState({
        phase: 'error',
        lastSyncAt: now,
        lastError: errors[0],
        errorCount: this.state.errorCount + 1,
      });
      this.scheduleHold('idle', ERROR_HOLD_MS);
    }
    return { errors };
  }

  private scheduleHold(nextPhase: SyncPhase, ms: number): void {
    this.cancelHold();
    this.holdTimer = setTimeout(() => {
      this.holdTimer = null;
      if (this.state.phase === 'syncing') return;
      this.setState({ phase: nextPhase });
    }, ms);
  }

  private cancelHold(): void {
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  getState(): SyncOrchestratorState {
    return this.state;
  }

  addListener(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private setState(partial: Partial<SyncOrchestratorState>): void {
    this.state = { ...this.state, ...partial };
    this.listeners.forEach((cb) => {
      try {
        cb(this.state);
      } catch (err) {
        console.warn('[SyncOrchestrator] listener error:', err);
      }
    });
  }
}

export const syncOrchestratorService = new SyncOrchestratorService();
